/**
 * The gym days behind Progress's chart, for the ledger under it.
 *
 * The chart aggregates into buckets — a week, a month — but the ledger below it lists
 * individual days, scoped to the same range and ordered newest first. Tapping a
 * multi-day bar highlights the days it stands for in this same list (`bucketKey`,
 * asked from core rather than reimplemented) rather than opening a second list of them.
 */

import type { ChartRange, Workout } from "@altrtrack/core";
import { rangeStart } from "@altrtrack/core";

/**
 * The gym days inside a range's window, newest first.
 *
 * The cutoff is core's `rangeStart`, asked rather than re-derived, so this list and the
 * chart above it always describe the same window — a local copy of "the 1st of the month"
 * would eventually include a day the chart did not count, with nothing to say which was
 * right (D-09).
 *
 * Days after `today` are excluded here as well as in `aggregate`, for the same reason:
 * a day dated in the future is not something the chart plots, so it must not be something
 * the list beneath the chart shows.
 */
export function daysInRange(
  workouts: readonly Workout[],
  range: ChartRange,
  today: string,
): Workout[] {
  const cutoff = rangeStart(range, today);
  return workouts
    .filter((w) => (cutoff === null || w.date >= cutoff) && w.date <= today)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
